/**
 * The "⋯" menu on a letter (a leaver row, an interim row, the draft header) with its one action:
 * mark the letter Done by hand, or take the mark back (David, Oct 15 2026). No confirmation: it is
 * reversible, and says who marked it. A click here never opens the row underneath.
 *
 * Not a design-system atom — the DS has no menu — so it composes the DS Button with a plain panel,
 * as `MultiSelect` does, closing on a click outside or Escape.
 */
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { CircleCheck, Loader2, MoreHorizontal, Undo2 } from 'lucide-react';
import { cn } from '../../../../shared';

/** The menu; `onToggle` does the work and throws a message to show when it fails. */
export function DoneMenu({ done, onToggle, label, className }: { done: boolean; onToggle: (done: boolean) => Promise<void>; label: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    // Not while it is saving: the answer, a failure included, is shown in the menu.
    const outside = (e: globalThis.MouseEvent) => {
      if (!busyRef.current && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && !busyRef.current && setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  /** Runs the one action; the menu closes once it has worked. */
  const act = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await onToggle(!done);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work. Try again.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    // The wrapper only stops a click from also opening the row; the buttons inside carry the keyboard.
    <div className={cn('relative inline-block text-left', className)} ref={ref} onClick={(e) => e.stopPropagation()}>
      <Button type="button" variant="ghost" size="sm" className="h-8 w-8 rounded px-0" aria-label={`More for ${label}`} aria-haspopup="menu" aria-expanded={open} onClick={() => {
          // An old failure belongs to the last attempt, not to the menu opened now.
          setError(null);
          setOpen((o) => !o);
        }}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-max min-w-[180px] rounded-md border border-solid border-pearl bg-white p-1 shadow-lg">
          <Button type="button" variant="ghost" size="sm" role="menuitem" disabled={busy} onClick={act} className="w-full rounded [&_.content]:justify-start">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : done ? <Undo2 className="mr-2 h-4 w-4" /> : <CircleCheck className="mr-2 h-4 w-4" />}
            {done ? 'Undo done' : 'Mark as done'}
          </Button>
          {error && <p role="alert" className="max-w-[16rem] px-2 py-1 text-xs text-red">{error}</p>}
        </div>
      )}
    </div>
  );
}
